import * as React from "react";

import { Icon } from "@blueprintjs/core";
import { Hit } from "instantsearch.js";
import uniqBy from "lodash/uniqBy";

import {
  USER_INDEX,
  WORKSPACE_GROUP_INDEX,
} from "@/components/pieces/algolia/indexes";
import { UnifiedSearch } from "@/components/pieces/search/unified-search/UnifiedSearch";
import { cn } from "@/lib/utils";
import { SharePermissionState } from "@/types/permissions";

import { PermissionDropdown } from "./PermissionDropdown";
import { ShareGroup, ShareUser } from "./types";

interface ShareSelectorProps {
  placeholder?: string;
  onChange: (users: ShareUser[], groups: ShareGroup[]) => void;
  showPermissionSelector?: boolean;
  selectedPermission?: SharePermissionState;
  onPermissionChange?: (permission: SharePermissionState) => void;
  availablePermissions?: SharePermissionState[];
  className?: string;
  searchIndices?: ("users" | "groups")[];
}

export function ShareSelector({
  placeholder,
  onChange,
  showPermissionSelector = false,
  selectedPermission = "VIEW",
  onPermissionChange,
  availablePermissions = ["VIEW", "EDIT", "SHARE"],
  className,
  searchIndices = ["users", "groups"],
}: ShareSelectorProps) {
  // Ensure selected permission is valid
  const validSelectedPermission = React.useMemo(() => {
    if (availablePermissions.length === 0) return "VIEW";
    return availablePermissions.includes(selectedPermission)
      ? selectedPermission
      : availablePermissions[0];
  }, [selectedPermission, availablePermissions]);
  const [internalSelections, setInternalSelections] = React.useState<Hit[]>([]);

  const processSelections = (selections: Hit[]) => {
    const users = selections.filter(
      (item) => item._index === USER_INDEX || "email" in item,
    );

    const groups = selections.filter(
      (item) =>
        item._index === WORKSPACE_GROUP_INDEX ||
        ("name" in item && !("email" in item)),
    );

    return { users, groups };
  };

  const handleSubmit = (selections: Hit[]) => {
    const { users, groups } = processSelections(selections);

    const uniqueUsers = uniqBy(users, "email").map((user) => ({
      id: user.id,
      email: user.email,
      name: user.name,
      avatar: user.avatar,
    })) as ShareUser[];

    const uniqueGroups = uniqBy(groups, "id").map((group) => ({
      id: group.id,
      name: group.name,
      workspaceName: group.workspace?.name,
    })) as ShareGroup[];

    setInternalSelections(selections);
    onChange(uniqueUsers, uniqueGroups);
  };

  const hasSelections = internalSelections.length > 0;

  // Build indices array based on searchIndices prop
  const indices: string[] = [];
  if (searchIndices.includes("users")) {
    indices.push(USER_INDEX);
  }
  if (searchIndices.includes("groups")) {
    indices.push(WORKSPACE_GROUP_INDEX);
  }

  // Determine default placeholder based on searchIndices
  const defaultPlaceholder = React.useMemo(() => {
    if (searchIndices.includes("users") && searchIndices.includes("groups")) {
      return "Add people and groups";
    } else if (searchIndices.includes("users")) {
      return "Add people";
    } else if (searchIndices.includes("groups")) {
      return "Add groups";
    }
    return "Search...";
  }, [searchIndices]);

  return (
    <div className={cn("flex gap-2", className)}>
      <div className="relative flex-1">
        <Icon
          icon="search"
          color="#6B7280"
          style={{
            position: "absolute",
            left: 10,
            top: 16,
            zIndex: 10,
            display: hasSelections ? "none" : "block",
          }}
        />

        <UnifiedSearch
          selections={internalSelections}
          onChange={handleSubmit}
          existedEmails={[]}
          invalidEmails={[]}
          placeholder={placeholder || defaultPlaceholder}
          canBeInvitedEmails={[]}
          pendingInvEmails={[]}
          indexName={indices}
          inputStyle={{
            fontSize: 16,
            paddingLeft: hasSelections ? 0 : 30,
            lineHeight: "24px",
          }}
        />
      </div>

      {showPermissionSelector && onPermissionChange && (
        <PermissionDropdown
          value={validSelectedPermission}
          onChange={onPermissionChange}
          availablePermissions={availablePermissions}
          showRemove={false}
          className="h-[48px]"
        />
      )}
    </div>
  );
}
